import { selectedTrackingGoals } from '@shared/util/tracking-plan';
import {
  projectApi,
  type Project,
  type ProjectTrackingPlan,
} from '@shared/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRight, ClipboardCopy, ExternalLink } from 'lucide-react';
import * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { SetupSignalStatus } from '@/components/setup-signal-status';
import { useWebsiteDiscovery } from '@/hooks/use-website-discovery';
import { DiscoveryProgress } from '@/components/discovery-progress';
import { useSetupSignals } from '@/hooks/use-setup-signals';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { ProjectAvatar } from '@/components/project-avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { buildAgentSetupInstructions } from '@/lib/agent-setup';
import { InstallSnippet } from '@/pages/overview/components/install-snippet';
import { TrackingChoices } from './tracking-choices';

export const ProjectInstallation = ({ project }: { project: Project }) => {
  const navigate = useNavigate();
  const discovery = useWebsiteDiscovery(`${project.id}:goals`);
  const queryClient = useQueryClient();
  const mobile = project.profile?.platform === 'mobile';
  const savedPlan = project.profile?.trackingPlan;
  const [editing, setEditing] = React.useState(!savedPlan);
  const [copyState, setCopyState] = React.useState<
    'idle' | 'copied' | 'manual'
  >('idle');
  const heading = React.useRef<HTMLHeadingElement>(null);
  React.useEffect(() => {
    if (!editing) {
      heading.current?.focus();
    }
  }, [editing]);
  const preparedQuery = useQuery({
    queryKey: ['setup-plan', project.id, savedPlan],
    queryFn: async () => {
      // Resuming reads the saved setup. Only explicit preparation creates keys/goals.
      const [keys, result] = await Promise.all([
        mobile
          ? Promise.resolve({ apiKeys: [] })
          : projectApi.listApiKeys(project.id),
        projectApi.listGoals(project.id),
      ]);
      const events = selectedTrackingGoals(savedPlan).map(
        (goal) => goal.eventName,
      );
      return {
        publicKey:
          keys.apiKeys.find((key) => !key.revokedAt)?.publicKey ?? null,
        profile: project.profile!,
        goals: result.goals.filter(
          (goal) =>
            goal.kind === 'event' && events.includes(goal.eventName ?? ''),
        ),
      };
    },
    enabled: Boolean(savedPlan) && !editing,
    staleTime: Infinity,
    retry: false,
  });
  const prepare = useMutation({
    mutationFn: (plan: ProjectTrackingPlan) =>
      projectApi.prepareSetupPlan(project.id, plan),
    onSuccess: (result) => {
      queryClient.setQueryData(
        ['setup-plan', project.id, result.profile.trackingPlan],
        result,
      );
      queryClient.setQueryData(['project', project.id], {
        ...project,
        profile: result.profile,
      });
      void queryClient.invalidateQueries({
        queryKey: ['project-goals', project.id],
      });
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
      setCopyState('idle');
      setEditing(false);
    },
  });
  const prepared = preparedQuery.data;
  const plan = prepared?.profile.trackingPlan;
  const goals = prepared?.goals ?? [];
  const signals = useSetupSignals(project, Boolean(prepared) && !editing);
  const ready = Boolean(plan) && signals.signalsReceived;
  let instructions = '';
  let instructionError = '';
  if (prepared && plan) {
    try {
      instructions = buildAgentSetupInstructions({
        project,
        publicKey: prepared.publicKey,
        plan,
        goals,
      });
    } catch (error) {
      instructionError =
        error instanceof Error
          ? error.message
          : 'Couldn’t prepare your instructions.';
    }
  }
  const copyForAgent = async () => {
    try {
      await navigator.clipboard.writeText(instructions);
      setCopyState('copied');
    } catch {
      setCopyState('manual');
    }
  };
  return (
    <div className="space-y-8">
      <div className="flex items-center gap-4">
        <ProjectAvatar
          name={project.name}
          logoUrl={project.profile?.logoUrl}
          className="size-14"
        />
        <div className="min-w-0">
          <p className="truncate font-semibold text-ink">{project.name}</p>
          <p className="truncate text-sm text-ink-muted">
            {project.primaryDomain ? `${project.primaryDomain} · ` : ''}
            {mobile
              ? 'Mobile'
              : project.profile?.platform === 'both'
                ? 'Web and mobile'
                : 'Web'}
          </p>
        </div>
      </div>
      {editing ? (
        <TrackingChoices
          initial={savedPlan}
          suggested={project.profile?.suggestedTrackingPlan}
          research={project.profile?.websiteResearch}
          onSuggest={
            project.profile?.websiteUrl
              ? () => discovery.run(project.profile!.websiteUrl!)
              : undefined
          }
          resumeAnalysis={discovery.isPending}
          analysisProgress={
            discovery.isPending ? (
              <DiscoveryProgress job={discovery.job} />
            ) : undefined
          }
          platform={project.profile?.platform ?? 'web'}
          pending={prepare.isPending}
          error={prepare.error?.message}
          onSubmit={(value) => {
            discovery.reset();
            prepare.mutate(value);
          }}
          onCancel={
            savedPlan
              ? () => {
                  prepare.reset();
                  setEditing(false);
                }
              : undefined
          }
        />
      ) : preparedQuery.isPending ? (
        <Skeleton className="h-72" />
      ) : preparedQuery.isError || !prepared || !plan ? (
        <Alert tone="error">
          We couldn’t prepare your setup.{' '}
          <button
            className="underline"
            onClick={() => void preparedQuery.refetch()}
          >
            Try again
          </button>
          <button className="ml-3 underline" onClick={() => setEditing(true)}>
            Review choices
          </button>
        </Alert>
      ) : (
        <>
          <div>
            <h1
              ref={heading}
              tabIndex={-1}
              className="text-3xl font-semibold tracking-tight text-ink outline-none"
            >
              Connect your coding agent
            </h1>
            <p className="mt-3 max-w-xl text-base leading-relaxed text-ink-muted">
              Paste the setup into your coding agent. It will install tracking
              and check that events arrive.
            </p>
          </div>
          <section
            aria-label="AI setup instructions"
            className="space-y-5 rounded-xl border border-accent/40 bg-accent-subtle p-5 sm:p-6"
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-sm font-semibold text-ink">
                Analytics setup
              </h2>
              <button
                className="text-sm font-medium text-accent underline underline-offset-4"
                onClick={() => {
                  prepare.reset();
                  setEditing(true);
                }}
              >
                Edit goals
              </button>
            </div>
            {instructionError ? (
              <Alert tone="error">
                {instructionError}{' '}
                <button className="underline" onClick={() => setEditing(true)}>
                  Prepare again
                </button>
              </Alert>
            ) : (
              <Button
                size="lg"
                className="h-auto min-h-12 w-full whitespace-normal py-3"
                onClick={() => void copyForAgent()}
              >
                <ClipboardCopy />
                {copyState === 'copied'
                  ? 'Copied — paste into your agent'
                  : 'Copy agent setup'}
              </Button>
            )}
            <p className="text-xs leading-relaxed text-ink-muted" role="status">
              {copyState === 'copied'
                ? 'We’ll check for incoming events here.'
                : 'Works with Codex, Cursor, Claude Code, or your preferred coding agent.'}
            </p>
            <details open={copyState === 'manual'}>
              <summary className="cursor-pointer text-sm text-ink">
                {copyState === 'manual'
                  ? 'Clipboard unavailable — select and copy below'
                  : 'Read the instructions'}
              </summary>
              <label className="mt-3 block">
                <span className="sr-only">AI setup instructions</span>
                <textarea
                  readOnly
                  value={instructions}
                  onFocus={(event) => event.target.select()}
                  className="h-72 w-full rounded-lg border border-border-strong bg-surface p-3 font-mono text-xs leading-relaxed text-ink"
                />
              </label>
            </details>
          </section>
          <SetupSignalStatus project={project} status={signals} />
          <details className="border-y border-border py-5">
            <summary className="cursor-pointer text-sm font-semibold text-ink">
              Custom configuration
            </summary>
            <div className="mt-5 space-y-5">
              <p className="text-sm text-ink-muted">
                Install manually or inspect exactly what your agent will
                configure.
              </p>
              {prepared.publicKey && (
                <div>
                  <h3 className="mb-3 text-sm font-medium text-ink">
                    Website script
                  </h3>
                  <InstallSnippet publicKey={prepared.publicKey} />
                </div>
              )}
              <dl className="space-y-3 text-sm">
                <div>
                  <dt className="font-medium text-ink">Allowed domains</dt>
                  <dd className="mt-1 break-words text-ink-muted">
                    {project.allowedHostnames?.join(', ') ||
                      'No domain restriction configured'}
                  </dd>
                </div>
                {goals.map((goal) => (
                  <div key={goal.id}>
                    <dt className="font-medium text-ink">{goal.name}</dt>
                    <dd className="mt-1 break-all font-mono text-xs text-ink-muted">
                      {goal.eventName}
                    </dd>
                  </div>
                ))}
              </dl>
              <div className="flex flex-wrap gap-4 text-sm">
                <Link
                  className="text-accent underline"
                  to={`/projects/${project.id}/details`}
                >
                  Edit project & goals
                </Link>
                <Link
                  className="text-accent underline"
                  to={`/projects/${project.id}/data-sources`}
                >
                  Data sources
                </Link>
              </div>
            </div>
          </details>
          {!mobile && project.profile?.websiteUrl && (
            <Button asChild variant="secondary">
              <a
                href={project.profile.websiteUrl}
                target="_blank"
                rel="noreferrer"
              >
                Open website <ExternalLink />
              </a>
            </Button>
          )}
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-6">
            <Button asChild variant="ghost">
              <Link to={`/projects/${project.id}/setup?edit=1`}>
                Edit product details
              </Link>
            </Button>
            <Button
              variant={ready ? 'primary' : 'secondary'}
              onClick={() =>
                navigate(`/projects/${project.id}/setup?step=growth`)
              }
            >
              Continue to integrations
              <ArrowRight />
            </Button>
          </div>
        </>
      )}
    </div>
  );
};
